import { paths } from '@common/types';
import { axiosInstance } from '@config/axios/axios.config';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

const createTeamRequest = async (
  props: paths['/team']['post']['requestBody']['content']['application/json'],
) => {
  const response = axiosInstance.post<
    paths['/team']['post']['requestBody']['content']['application/json'],
    AxiosResponse<
      paths['/team']['post']['responses']['201']['content']['application/json']
    >
  >(`/team`, {
    ...props,
  });
  const data = await response;

  return data;
};

export const useCreateTeamMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['team'],
    mutationFn: (
      props: paths['/team']['post']['requestBody']['content']['application/json'],
    ) => createTeamRequest(props),
    onSuccess: () => client.invalidateQueries({ queryKey: ['teams'] }),
  });

  return mutation;
};

const transferTeamMemberRequest = async (
  props: paths['/team/{team_id}/transfer']['post']['requestBody']['content']['application/json'] & {
    team_id: string;
  },
) => {
  const response = axiosInstance.post<
    paths['/team/{team_id}/transfer']['post']['requestBody']['content']['application/json'],
    paths['/team/{team_id}/transfer']['post']['responses']['200']['content']
  >(`/team/${props.team_id}/transfer`, {
    user_ids: props.user_ids,
    team2_id: props.team2_id,
  });
  const data = await response;

  return data;
};

export const useTransferTeamMemberMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['transferTeam'],
    mutationFn: (
      props: paths['/team/{team_id}/transfer']['post']['requestBody']['content']['application/json'] & {
        team_id: string;
      },
    ) => transferTeamMemberRequest(props),
    onSuccess: (_, variables) => {
      const invalidationPromises = [
        client.invalidateQueries({ queryKey: ['teams'] }),

        ...variables.user_ids.map(userId =>
          client.invalidateQueries({ queryKey: ['user', userId] }),
        ),
        client.invalidateQueries({ queryKey: ['users'] }),
      ];

      return Promise.all(invalidationPromises);
    },
  });

  return mutation;
};

const assignUserTeamRequest = async (
  props: paths['/team/{team_id}/assign']['post']['requestBody']['content']['application/json'] & {
    team_id: string;
  },
) => {
  const response = axiosInstance.post<
    paths['/team/{team_id}/assign']['post']['requestBody']['content']['application/json'],
    paths['/team/{team_id}/assign']['post']['responses']['201']['content']
  >(`/team/${props.team_id}/assign`, {
    user_ids: props.user_ids,
  });
  const data = await response;

  return data;
};

export const useAssignUserTeamMutation = ({ id }: { id: string }) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['team'],
    mutationFn: (
      props: paths['/team/{team_id}/assign']['post']['requestBody']['content']['application/json'] & {
        team_id: string;
      },
    ) => assignUserTeamRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['teams'] }),
        client.invalidateQueries({ queryKey: ['team', id] }),
        client.invalidateQueries({ queryKey: ['teamEligibleUsers', id] }),
      ]),
  });

  return mutation;
};

const assignLeadUserRequest = async (
  props: paths['/team/{team_id}/assign-lead']['post']['requestBody']['content']['application/json'] &
    paths['/team/{team_id}/assign-lead']['post']['parameters']['path'],
) => {
  const response = axiosInstance.post<
    paths['/team/{team_id}/assign-lead']['post']['requestBody']['content']['application/json'],
    paths['/team/{team_id}/assign-lead']['post']['responses']['201']['content']
  >(`/team/${props.team_id}/assign-lead`, {
    user_id: props.user_id,
  });
  const data = await response;

  return data;
};

export const useAssignLeadUserMutation = () => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['assignTeamLead'],
    mutationFn: assignLeadUserRequest,
    onSuccess: (_, variables) =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['teams'] }),
        client.invalidateQueries({ queryKey: ['team', variables.team_id] }),
        client.invalidateQueries({
          queryKey: ['team', 'history', variables.team_id],
        }),
      ]),
  });

  return mutation;
};

const manageTeamRequest = async (
  props: paths['/team/{team_id}']['patch']['requestBody']['content']['application/json'] & {
    team_id: string;
  },
) => {
  const response = axiosInstance.patch<
    paths['/team/{team_id}']['patch']['requestBody']['content']['application/json'],
    paths['/team/{team_id}']['patch']['responses']['200']['content']
  >(`/team/${props.team_id}`, {
    name: props.name,
    description: props.description,
    record_id: props.record_id,
    record_ids: props.record_ids,
    city: props.city,
    state: props.state,
    country: props.country,
  });
  const data = await response;

  return data;
};

export const useManageTeamRequest = ({ id }: { id: string }) => {
  const client = useQueryClient();

  const mutation = useMutation({
    mutationKey: ['team'],
    mutationFn: (
      props: paths['/team/{team_id}']['patch']['requestBody']['content']['application/json'] & {
        team_id: string;
      },
    ) => manageTeamRequest(props),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: ['teams'] }),
        client.invalidateQueries({ queryKey: ['team', id] }),
      ]),
  });

  return mutation;
};

export const teamsCsvDownloadRequest = async (
  props: paths['/record/teams/csv']['get']['parameters']['query'],
) => {
  const response = await axiosInstance.get(`/record/teams/csv`, {
    headers: {
      'Content-type': 'application/xml',
    },
    responseType: 'blob',
    params: props,
  });

  const data = (await response).data;

  return data;
};

export const useTeamsCsvDownloadQuery = () => {
  const mutation = useMutation({
    mutationKey: ['teamsCSVDownload'],
    mutationFn: (
      props: paths['/record/teams/csv']['get']['parameters']['query'],
    ) => teamsCsvDownloadRequest(props),
  });

  return mutation;
};
